import { DollarOutlined } from '@ant-design/icons';
import commaNumber from 'comma-number';
import type { MouseEvent } from 'react';
import styled from 'styled-components';

import { Flex, VFlex } from '@/components/base';
import ProgressBar from '@/components/Chart/ProgressBar/ProgressBar';
import Typo from '@/components/Typography/Typo';
import { TOPIC_TREND_CATEGORY } from '@/features/GlobalSearch/types/searchConst';
import useTopicLevelIcon from '@/features/Taxonomy/hooks/useTopicLevelIcon';
import taxonomyUtils from '@/features/Taxonomy/utils/taxonomy';
import useColor from '@/hooks/domain/useColor';
import { radius } from '@/styles/sizes';

type Props = {
  topic: TopicTrendItemDTO;
  category: TOPIC_TREND_CATEGORY;
  maxValue: number;
  onClick: (e: MouseEvent) => void;
};

const TopicTrendItem = (props: Props) => {
  const { topic, category, maxValue } = props;
  const topicPaths = taxonomyUtils.getTopicPath(topic).map((t) => t.label);
  const getTopicLevelIcon = useTopicLevelIcon();

  return (
    <Style g={8} onClick={props.onClick}>
      <Flex j="space-between">
        <Flex g={12} a="center">
          {getTopicLevelIcon(topic.level)}
          <Typo type="label2">{topic.name}</Typo>
        </Flex>
        <ValueDisplay
          value={topic.value}
          category={category}
          maxValue={maxValue}
        />
      </Flex>
      <Flex>
        <Typo type="label3" className="text-gray-700">
          {topicPaths.join(' / ')}
        </Typo>
      </Flex>
    </Style>
  );
};

const Style = styled(VFlex)`
  padding: 12px;
  border-radius: ${radius.token.lg}px;
  cursor: pointer;
  &:hover {
    background-color: ${(props) => props.theme.colors.gray200};
  }
`;

const ValueDisplay = ({
  value,
  category,
  maxValue,
}: {
  value: number;
  category: TOPIC_TREND_CATEGORY;
  maxValue: number;
}) => {
  const color = useColor();
  const fixedValue = commaNumber(Number(value.toFixed(1)));
  switch (category) {
    case TOPIC_TREND_CATEGORY.VOLUME:
      return (
        <Flex a="center" g={6}>
          <Typo type="label3">{fixedValue}</Typo>
          <ProgressBar
            current={value}
            total={maxValue}
            style={{ width: 70, height: 12 }}
          />
        </Flex>
      );
    case TOPIC_TREND_CATEGORY.NEGATIVE_RATIO:
      return (
        <Flex a="center" g={6}>
          <Typo type="label3" className="text-destructive">
            {fixedValue}%
          </Typo>
          <ProgressBar
            current={value}
            total={100}
            style={{ width: 70, height: 12 }}
            barStyle={{ backgroundColor: color.red600 }}
          />
        </Flex>
      );
    case TOPIC_TREND_CATEGORY.REVENUE_IMPACT:
      return (
        <Flex a="center" g={6}>
          <Flex a="center" g={6}>
            <DollarOutlined />
            <Typo type="label3">{fixedValue}</Typo>
          </Flex>
          <ProgressBar
            current={value}
            total={maxValue}
            style={{ width: 70, height: 12 }}
          />
        </Flex>
      );
    default:
      throw new Error('Invalid category', category);
  }
};

export default TopicTrendItem;
